import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

function Dashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statusSummary, setStatusSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/dashboard/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats);
        setStatusSummary(data.statusSummary);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const allStatuses = [
    'Phone', 'Email', 'PAN done', 'Other details done', 
    'Address done', 'Aadhar Done', 'Payment done', 
    'Nominee done', 'VKYC done'
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">42Cards Solutions DSA Portal</h1>
          <div className="flex items-center">
            <span className="mr-4 text-gray-600">Welcome, {currentUser?.name}</span>
            <button
              onClick={handleLogout}
              className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition duration-200"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center">
              <div className="flex space-x-4">
                <Link
                  to="/dashboard"
                  className="text-white px-3 py-2 rounded-md text-sm font-medium bg-gray-900"
                >
                  Dashboard
                </Link>
                <Link
                  to="/generate-lead"
                  className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  Generate Lead
                </Link>
                <Link
                  to="/leads"
                  className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  My Leads
                </Link>
                <Link
                  to="/help"
                  className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  Help
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main>
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-6">Welcome to Your Dashboard</h2>
              
              {loading ? (
                <div className="text-center py-10">
                  <p className="text-gray-500">Loading dashboard...</p>
                </div>
              ) : (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                      <h3 className="text-lg font-medium text-blue-800">Total Leads</h3>
                      <p className="text-3xl font-bold">{stats?.totalLeads || 0}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                      <h3 className="text-lg font-medium text-green-800">Converted Leads</h3>
                      <p className="text-3xl font-bold">{stats?.convertedLeads || 0}</p>
                      <p className="text-sm text-green-600 mt-1">VKYC Completed</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
                      <h3 className="text-lg font-medium text-yellow-800">Pending Leads</h3>
                      <p className="text-3xl font-bold">{stats?.pendingLeads || 0}</p>
                    </div>
                  </div>

                  {/* Status Summary */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium mb-4">Lead Status Summary</h3>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {allStatuses.map(status => (
                          <div key={status} className="bg-white p-3 rounded border border-gray-200">
                            <p className="text-xs text-gray-600 mb-1">{status}</p>
                            <p className="text-2xl font-bold text-gray-800">
                              {statusSummary?.[status] || 0}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <h3 className="text-lg font-medium mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Link
                      to="/generate-lead"
                      className="bg-blue-600 text-white p-4 rounded-lg flex items-center justify-center hover:bg-blue-700 transition duration-200"
                    >
                      Generate New Lead
                    </Link>
                    <Link
                      to="/leads"
                      className="bg-gray-600 text-white p-4 rounded-lg flex items-center justify-center hover:bg-gray-700 transition duration-200"
                    >
                      View All Leads
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;